// src/services/bgmManager.js - BGM 매니저 클래스 (BGM 재생 오류 해결 버전)

import { debugLog, errorLog } from '../utils/environment'; // [추가] 조건부 로깅

class BGMManager {
  constructor() {
    this.lobbyBGM = null;
    this.gameBGMs = [];
    this.currentGameBGM = null;
    this.currentGameBGMIndex = -1;
    this.isPlaying = false;
    this.currentContext = null; // 'lobby' or 'game'
    // 🔧 [수정] 오디오 기본 설정 ON - 처음 방문하는 사용자는 BGM 자동 재생
    const savedSoundSetting = localStorage.getItem('painori-sound-enabled');
    if (savedSoundSetting === null) {
      // 처음 방문하는 사용자는 BGM OFF로 설정 (사용자 요청)
      localStorage.setItem('painori-sound-enabled', 'false');
      this.enabled = false;
    } else {
      this.enabled = savedSoundSetting === 'true';
    }
    this.userInteracted = false; // 사용자 상호작용 여부

    // 🆕 BGM 재생 큐잉 시스템 추가 - 중복 재생 방지
    this.playQueue = [];
    this.isTransitioning = false;
    this.lastPlayRequest = null;

    // 🚀 [최적화] 프로덕션 도메인 사용으로 성능 개선
    this.baseUrl = 'https://sounds.spot.painori.com/';

    // 게임 BGM 파일명들
    this.gameBGMFiles = ['game_bgm_01.ogg', 'game_bgm_02.ogg', 'game_bgm_03.ogg'];

    // 🔍 디버깅 - 초기화 확인
    debugLog('[BGM Debug] BGMManager 초기화');
    debugLog('[BGM Debug] baseUrl:', this.baseUrl);
    debugLog('[BGM Debug] enabled:', this.enabled);
    debugLog('[BGM Debug] gameBGMFiles:', this.gameBGMFiles);

    // 🆕 [추가] 자동 재생 로직 - 페이지 로딩 후 자동으로 로비 BGM 재생
    if (this.enabled) {
      // 약간의 지연 후 자동 재생 (페이지 로딩 완료 후)
      setTimeout(() => {
        this.userInteracted = true; // 자동 재생을 위해 상호작용 플래그 설정
        this.playLobbyBGM();
        debugLog('[BGM] 자동 재생 시작');
      }, 2000); // 2초 후 자동 재생
    }
  }

  // 사운드 설정 토글 - 수정됨: 켤 때 현재 컨텍스트에 맞는 BGM 재생
  toggleSound() {
    this.enabled = !this.enabled;
    localStorage.setItem('painori-sound-enabled', this.enabled);

    // [수정] 사용자 상호작용 기록
    this.userInteracted = true;

    if (!this.enabled) {
      // 사운드 끄기
      this.stopAll();
    } else {
      // 사운드 켜기 - 현재 컨텍스트에 맞는 BGM 재생
      debugLog('[BGM] 사운드 켜짐, 현재 컨텍스트:', this.currentContext);

      if (this.currentContext === 'lobby') {
        this.playLobbyBGM();
      } else if (this.currentContext === 'game') {
        // 게임 중이면 현재 선택된 BGM 재생
        if (this.currentGameBGMIndex >= 0) {
          this.resumeGameBGM();
        } else {
          this.playGameBGM();
        }
      }
    }

    return this.enabled;
  }

  // 모든 BGM 정지
  stopAll() {
    if (this.lobbyBGM) {
      this.lobbyBGM.pause();
      this.lobbyBGM.currentTime = 0;
    }

    if (this.currentGameBGM) {
      this.currentGameBGM.pause();
      this.currentGameBGM.currentTime = 0;
    }

    this.isPlaying = false;
    debugLog('[BGM] 모든 BGM 정지');
  }

  // 🆕 로비 BGM 재생 - 큐잉 시스템 적용하여 중복 재생 방지
  async playLobbyBGM() {
    if (!this.enabled) return;

    // [수정] 사용자 상호작용 전에는 재생하지 않음
    if (!this.userInteracted) {
      debugLog('[BGM] 사용자 상호작용 전 - 재생 건너뛰기');
      return;
    }

    // 🆕 중복 요청 방지 로직
    // 🆕 중복 요청 방지 로직
    // const requestId = 'lobby-' + Date.now(); // Unused
    if (this.lastPlayRequest === 'lobby' && this.isTransitioning) {
      debugLog('[BGM] 로비 BGM 재생 요청 무시 (이미 전환 중)');
      return;
    }

    this.lastPlayRequest = 'lobby';
    this.isTransitioning = true;

    try {
      // 게임 BGM이 재생 중이면 먼저 안전하게 정지
      await this.stopGameBGMSafely();

      this.currentContext = 'lobby';

      if (!this.lobbyBGM) {
        const lobbyUrl = `${this.baseUrl}lobby_bgm.ogg`;

        // 🔍 디버깅 코드 추가
        debugLog('[BGM Debug] ===== 로비 BGM 재생 시도 =====');
        debugLog('[BGM Debug] URL:', lobbyUrl);

        this.lobbyBGM = new Audio(lobbyUrl);
        this.lobbyBGM.loop = true;
        this.lobbyBGM.volume = 0.3;
      }

      // 🆕 안전한 재생 - 이미 재생 중이 아닐 때만 재생
      if (this.lobbyBGM.paused) {
        await this.lobbyBGM.play();
        this.isPlaying = true;
        debugLog('[BGM] 로비 BGM 재생 시작');
      } else {
        debugLog('[BGM] 로비 BGM 이미 재생 중');
      }
    } catch (error) {
      errorLog('[BGM] 로비 BGM 재생 실패:', error);
      errorLog('[BGM Debug] 에러 타입:', error.name);
      errorLog('[BGM Debug] 에러 메시지:', error.message);

      // [수정] 404 오류 시 재시도 금지
      if (error.name === 'NotAllowedError' || error.message.includes('404')) {
        debugLog('[BGM] 재생 실패 - 재시도 금지');
        this.isPlaying = false;
      }
    } finally {
      // 🆕 전환 상태 해제 - 500ms 후에 다른 요청 허용
      setTimeout(() => {
        this.isTransitioning = false;
      }, 500);
    }
  }

  // 로비 BGM 정지
  stopLobbyBGM() {
    if (this.lobbyBGM) {
      this.lobbyBGM.pause();
      this.lobbyBGM.currentTime = 0;
      debugLog('[BGM] 로비 BGM 정지');
    }
  }

  // 게임 BGM 재생 - 수정됨: 게임 중에는 같은 BGM 유지
  async playGameBGM() {
    if (!this.enabled) return;

    // 로비 BGM 정지
    this.stopLobbyBGM();

    this.currentContext = 'game';

    // 이미 선택된 게임 BGM이 있고 재생 가능한 상태면 재개
    if (this.currentGameBGM && this.currentGameBGMIndex >= 0) {
      try {
        await this.currentGameBGM.play();
        this.isPlaying = true;
        debugLog('[BGM] 기존 게임 BGM 재개:', this.gameBGMFiles[this.currentGameBGMIndex]);
        return;
      } catch (error) {
        debugLog('[BGM] 기존 BGM 재개 실패, 새로 선택');
      }
    }

    // 새로운 BGM 선택
    await this.selectNextGameBGM();
  }

  // 게임 BGM 재개 (사운드 토글 시 사용)
  async resumeGameBGM() {
    if (!this.enabled || !this.currentGameBGM) return;

    try {
      await this.currentGameBGM.play();
      this.isPlaying = true;
      debugLog('[BGM] 게임 BGM 재개');
    } catch (error) {
      errorLog('[BGM] 게임 BGM 재개 실패:', error);
      // 재개 실패 시 새로 선택
      await this.selectNextGameBGM();
    }
  }

  // 다음 게임 BGM 선택 및 재생 - 수정됨: 게임 세션 중에는 호출되지 않음
  async selectNextGameBGM() {
    if (!this.enabled || this.currentContext !== 'game') return;

    // 이전과 다른 인덱스 선택
    let nextIndex;
    do {
      nextIndex = Math.floor(Math.random() * this.gameBGMFiles.length);
    } while (nextIndex === this.currentGameBGMIndex && this.gameBGMFiles.length > 1);

    this.currentGameBGMIndex = nextIndex;

    try {
      // 이전 BGM 정지
      if (this.currentGameBGM) {
        this.currentGameBGM.pause();
        this.currentGameBGM = null;
      }

      // 새 BGM 생성 및 재생
      const bgmUrl = `${this.baseUrl}${this.gameBGMFiles[nextIndex]}`;

      // 🔍 디버깅 코드 추가
      debugLog('[BGM Debug] ===== BGM 재생 시도 =====');
      debugLog('[BGM Debug] URL:', bgmUrl);
      debugLog('[BGM Debug] 파일명:', this.gameBGMFiles[nextIndex]);

      // [수정] URL 직접 테스트 로그 제거 - 개발 환경에서만 필요시 활성화
      // fetch 관련 디버깅 코드는 필요시에만 활성화

      this.currentGameBGM = new Audio(bgmUrl);
      this.currentGameBGM.volume = 0.3;
      this.currentGameBGM.loop = true; // 무한 반복으로 변경

      // ended 이벤트 제거 - 무한 반복이므로 필요 없음

      await this.currentGameBGM.play();
      this.isPlaying = true;
      debugLog(`[BGM] 게임 BGM 재생: ${this.gameBGMFiles[nextIndex]}`);
    } catch (error) {
      errorLog('[BGM] 게임 BGM 재생 실패:', error);
      errorLog('[BGM Debug] 에러 타입:', error.name);
      errorLog('[BGM Debug] 에러 메시지:', error.message);
    }
  }

  // 게임 BGM 정지 - 수정됨: 게임 세션 종료 시 인덱스 리셋
  stopGameBGM() {
    if (this.currentGameBGM) {
      this.currentGameBGM.pause();
      this.currentGameBGM.currentTime = 0;
      debugLog('[BGM] 게임 BGM 정지');
    }
    // 로비로 돌아갈 때만 인덱스 리셋
    if (this.currentContext === 'lobby') {
      this.currentGameBGMIndex = -1;
      this.currentGameBGM = null;
    }
  }

  // 🆕 안전한 게임 BGM 정지 - 로비 BGM 재생 시 충돌 방지
  async stopGameBGMSafely() {
    if (this.currentGameBGM && !this.currentGameBGM.paused) {
      try {
        this.currentGameBGM.pause();
        this.currentGameBGM.currentTime = 0;
        debugLog('[BGM] 게임 BGM 안전하게 정지');

        // 완전히 정지될 때까지 잠시 대기
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        debugLog('[BGM] 게임 BGM 정지 중 오류 (무시):', error.message);
      }
    }
  }

  // 현재 재생 중인지 확인
  isCurrentlyPlaying() {
    return this.isPlaying && this.enabled;
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const bgmManager = new BGMManager();

// 🆕 [추가] 전역 접근을 위한 window 객체 노출
if (typeof window !== 'undefined') {
  window.bgmManager = bgmManager;
  debugLog('[BGM] 전역 bgmManager 객체 노출 완료');
}

export default bgmManager;